import { IProblemListItem } from '../../../../../modules/API/TeudotProblems/models/problems'


export interface IProblemTypeAllowances {
  weightAllowed: boolean
  quantityAllowed: boolean
  priceAllowed: boolean
}

/**
 * Result of problem cost calculation
 */
export interface IProblemCostResult {
  difference: number  // הפרש (תמיד חיובי)
  isLoss: boolean     // האם זה הפסד (true) או זיכוי (false)
  label: string       // תיאור קצר בעברית
}

/**
 * Calculate problem cost based on problem type
 */
export const calculateProblemCost = (
  problem: {
    Type: number
    Weight: number
    Price: number
    Quantity: number
    WeightRequested: number
    PriceRequested: number
    QuantityRequested: number
  },
  originalInfo: {
    Weight: number
    Price: number
    Quantity: number
  }
): IProblemCostResult => {
  const originalTotal = originalInfo.Price * originalInfo.Weight
  
  const weightPerUnit = problem.Quantity > 0 
    ? problem.Weight / problem.Quantity 
    : originalInfo.Quantity > 0 
      ? originalInfo.Weight / originalInfo.Quantity 
      : 0

  switch (problem.Type) {
    case 1: {
      // Wrong quantity - calculation based on what was received
      const receivedWeight = problem.WeightRequested || (problem.QuantityRequested * weightPerUnit)
      const price = problem.PriceRequested || problem.Price || originalInfo.Price
      const newTotal = receivedWeight * price
      const diff = Math.abs(originalTotal - newTotal)
      
      return {
        difference: +diff.toFixed(2),
        isLoss: newTotal < originalTotal,
        label: newTotal < originalTotal ? 'זיכוי לקוח' : 'חיוב לקוח'
      }
    }
    
    case 2: {
      // Wasted - loss
      const wastedWeight = problem.WeightRequested || (problem.QuantityRequested * weightPerUnit)
      const price = problem.PriceRequested || problem.Price || originalInfo.Price
      const wastedValue = wastedWeight * price
      
      return {
        difference: +wastedValue.toFixed(2),
        isLoss: true,
        label: 'זיכוי לקוח'
      }
    }
    
    case 3: {
      // Return - deduction
      const returnedWeight = problem.WeightRequested || (problem.QuantityRequested * weightPerUnit)
      const price = problem.PriceRequested || problem.Price || originalInfo.Price
      const returnValue = returnedWeight * price
      
      return {
        difference: +returnValue.toFixed(2),
        isLoss: true,
        label: 'זיכוי לקוח'
      }
    }
    
    
    case 4: {
      // Price difference
      const newPrice = problem.PriceRequested || problem.Price || originalInfo.Price
      const weight = problem.WeightRequested || problem.Weight || originalInfo.Weight
      const newTotal = newPrice * weight
      const diff = Math.abs(originalTotal - newTotal)
      
      return {
        difference: +diff.toFixed(2),
        isLoss: newTotal < originalTotal,
        label: newTotal > originalTotal ? 'חיוב לקוח' : 'זיכוי לקוח'
      }
    }
    
    default: {
      const weight = problem.WeightRequested || problem.Weight || originalInfo.Weight
      const price = problem.PriceRequested || problem.Price || originalInfo.Price
      const newTotal = weight * price
      const diff = Math.abs(originalTotal - newTotal)
      return {
        difference: +diff.toFixed(2),
        isLoss: newTotal < originalTotal,
        label: 'הפרש'
      }
    }
  }
}


export const getProblemTypeAllowances = (
  problemTypeId: number,
  problemTypes: IProblemListItem[] | null
): IProblemTypeAllowances => {
  // Default to all allowed if no problem types available or invalid ID
  if (!problemTypes || problemTypeId <= 0) {
    return {
      weightAllowed: true,
      quantityAllowed: true,
      priceAllowed: true
    }
  }

  const problemType = problemTypes.find(pt => pt.Code === problemTypeId)
  return {
    weightAllowed: problemType?.WeightAllowedToFill ?? true,
    quantityAllowed: problemType?.QuantityAllowedToFill ?? true,
    priceAllowed: problemType?.PriceAllowedToFill ?? true
  }
}

export const ProblemDisclaimerText = (problemTypeCode: number): string | null => {
  switch (problemTypeCode) {
    case 1:
      // не правильное колличество
      return "פניה 'התקבלה כמות שונה' - יש לרשום כמות שהתקבלה"
    case 2:
      //  утилизация
      return "פניה 'נזרק' - יש לרשום כמות שזרקת"
    case 3:
      
      //  возврат
      return "פניה 'החזרה' - יש לרשום כמות שהוחזרה"
  }
  return null
}

/**
 * Gets display titles for input fields (always returns all for consistency)
 * 
 * @returns Array of input field titles
 */
export const getInputFieldTitles = (allowences: IProblemTypeAllowances): string[] => {
  const titles: string[] = []
  // if (allowences.quantityAllowed) {
  //   titles.push('כמות')
  // }
  // if (allowences.weightAllowed) {
  //   titles.push('משקל')
  // }
  // if (allowences.priceAllowed) {
  //   titles.push('מחיר')
  // }

  // return titles
  return ['כמות', 'משקל', 'מחיר']
}

/**
 * Validates if form data is complete based on problem type allowances
 * 
 * @param problems - Array of problem form data
 * @param problemTypes - Array of available problem types
 * @returns Boolean indicating if all required fields are filled
 */
export const validateProblemsFormData = (
  problems: Array<{
    Type: number
    WeightRequested: number
    QuantityRequested: number
    PriceRequested: number
    isAllowToSaveEmpty?: boolean
  }>,
  problemTypes: IProblemListItem[] | null
): boolean => {
  return problems.every(problem => {
    if (problem.Type <= 0) return false
    
    const allowances = getProblemTypeAllowances(problem.Type, problemTypes)
    
    // Only validate fields that are allowed for this problem type
    
    if (problem.Type === 1) {
      return problem.isAllowToSaveEmpty ? true : problem.WeightRequested > 0 || problem.QuantityRequested > 0 || problem.PriceRequested > 0
    }
    return problem.WeightRequested > 0 || problem.QuantityRequested > 0 || problem.PriceRequested > 0
  })
  
} 